import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Search, Sparkles, ArrowRight, ArrowLeft, Loader2, Inbox, Rocket, CalendarDays, Users, FileText, Stethoscope,
  ShieldAlert, Video, Wallet, UserCog, Settings, BookOpen, MessageSquarePlus, LifeBuoy, X, ChevronRight,
} from "lucide-react";
import DashboardLayout from "../layouts/DashboardLayout";
import HelpArticleCard from "../components/help/HelpArticleCard";
import SendRequestCard from "../components/help/SendRequestCard";
import MyRequests from "../components/help/MyRequests";
import { currentRole } from "../components/help/requestLabels";
import api from "../api";

// "Where do I do X?" help for every staff role. The library is the backend's
// help_content/articles.json, already filtered to this clinic's features and
// this user's role (help/routes.py). Typing shows matching articles
// instantly; Ask gets a short answer written from those same articles.

const STOP = new Set("a an the i me my we our you your is are am be to of in on for and or do does did how can could where what when which who why it this that with from at by as if so there set up get want need should would will please find some any one way".split(" "));
const tokens = (t) => (t || "").toLowerCase().match(/[a-z0-9][a-z0-9-]*/g)?.filter((w) => w.length > 1 && !STOP.has(w)) || [];

// Same ranking idea as help/library.py's search().
function rank(query, articles) {
  const q = query.toLowerCase();
  const words = new Set(tokens(q));
  if (!words.size) return [];
  return articles
    .map((a) => {
      let score = 0;
      (a.keywords || []).forEach((k) => {
        const kw = k.toLowerCase();
        if (kw.includes(" ") ? q.includes(kw) : words.has(kw)) score += kw.includes(" ") ? 6 : 4;
        else if ([...words].some((w) => w.length >= 4 && kw.startsWith(w))) score += 2;
      });
      tokens(a.title).forEach((t) => { if (words.has(t)) score += 3; });
      const body = new Set(tokens(`${a.answer} ${(a.steps || []).join(" ")}`));
      words.forEach((w) => { if (body.has(w)) score += 1; });
      return { a, score };
    })
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score)
    .slice(0, 6)
    .map((x) => x.a);
}

const SUGGESTIONS = [
  "How do I add a holiday?",
  "Where do I set my fees?",
  "How do patients pay at the clinic?",
  "How do I send a PHQ-9?",
  "How do I add a receptionist?",
  "Where is my booking link?",
];

// One icon and a one-line description per topic (categories in articles.json).
const TOPICS = {
  "Getting set up": [Rocket, "Profiles, working hours, booking links"],
  "Appointments & calendar": [CalendarDays, "Booking, rescheduling, holidays, time off"],
  "Patients": [Users, "Adding patients, records and messages"],
  "Consultations & notes": [FileText, "Writing, signing and printing notes"],
  "Clinical tools": [Stethoscope, "Questionnaires, homework, treatment plans"],
  "Safety & risk": [ShieldAlert, "Risk assessments, crisis alerts, safety plans"],
  "Video sessions": [Video, "Online visits and session transcripts"],
  "Payments & finance": [Wallet, "Fees, online payments, payouts, plans"],
  "Team & access": [UserCog, "Staff logins, roles, supervisors"],
  "Settings": [Settings, "Letterhead, policies and patient alerts"],
};

function TopicTile({ name, articles, onOpen }) {
  const [Icon, blurb] = TOPICS[name] || [BookOpen, ""];
  return (
    <button type="button" onClick={onOpen}
      className="group text-left rounded-2xl border border-secondary-200 bg-white p-4 hover:border-primary-300 hover:shadow-md transition-all flex flex-col gap-3 min-w-0">
      <div className="flex items-center justify-between">
        <span className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center group-hover:bg-primary-600 group-hover:text-white transition-colors">
          <Icon className="w-5 h-5" />
        </span>
        <span className="text-xs font-medium text-secondary-500">{articles.length} article{articles.length === 1 ? "" : "s"}</span>
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-secondary-900">{name}</p>
        {blurb && <p className="text-sm text-secondary-500 mt-0.5">{blurb}</p>}
      </div>
      <ul className="space-y-1 text-sm text-secondary-600 border-t border-secondary-100 pt-3">
        {articles.slice(0, 2).map((a) => <li key={a.id} className="truncate">{a.title}</li>)}
      </ul>
      <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary-700">
        Browse <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </button>
  );
}

export default function Help() {
  const navigate = useNavigate();
  const location = useLocation();
  const [library, setLibrary] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState(() => new URLSearchParams(location.search).get("q") || "");
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState(null); // {question, answer, articles, found, source, notice, request}
  const [category, setCategory] = useState(null);
  const [requestsVersion, setRequestsVersion] = useState(0);
  const [askingSupport, setAskingSupport] = useState(false);
  const [teamOpen, setTeamOpen] = useState(null);
  const isAdmin = ["admin", "ops"].includes(currentRole());

  useEffect(() => {
    api.fetchHelpArticles().then((r) => setLibrary(r.data)).catch(() => setError("Couldn't load help articles."));
  }, []);
  useEffect(() => {
    if (!isAdmin) return;
    api.fetchTeamHelpRequests()
      .then((r) => setTeamOpen((r.data.requests || []).filter((x) => ["new", "reviewing"].includes(x.status)).length))
      .catch(() => setTeamOpen(null));
  }, [isAdmin]);

  const articles = library?.articles || [];
  const instant = useMemo(() => (query.trim().length >= 3 ? rank(query, articles) : []), [query, articles]);
  const categories = (library?.categories || []).filter((c) => articles.some((a) => a.category === c));
  const sent = () => setRequestsVersion((v) => v + 1);

  const ask = async (q = query) => {
    const question = q.trim();
    if (!question || asking) return;
    setQuery(question);
    setCategory(null);
    setAsking(true);
    try {
      const res = await api.askHelp(question);
      setResult({ question, ...res.data });
    } catch (e) {
      setResult({ question, answer: null, articles: rank(question, articles), found: false, source: "search",
        request: { kind: "other" },
        notice: e?.response?.data?.error || "Couldn't reach the help assistant, so these are search results." });
    } finally {
      setAsking(false);
    }
  };
  const clear = () => { setQuery(""); setResult(null); };
  const goLinks = (result?.articles || []).filter((a) => a.link?.path && a.link.path !== "/help");

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8 pb-12">
        {/* Search */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-700 via-primary-600 to-primary-500 text-white px-5 py-8 md:px-10 md:py-12 shadow-xl">
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-28 -left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)", backgroundSize: "18px 18px" }} />

          <div className="relative max-w-3xl">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold tracking-wide">
              <LifeBuoy className="w-3.5 h-3.5" /> Help centre
            </p>
            <h1 className="mt-4 text-3xl md:text-4xl font-heading font-bold tracking-tight">What do you need to do?</h1>
            <p className="mt-2 text-white/80 max-w-xl">Ask in your own words and get the exact screen and steps, or browse by topic.</p>

            <form className="mt-6" onSubmit={(e) => { e.preventDefault(); ask(); }}>
              <label htmlFor="help-question" className="sr-only">Your question</label>
              <div className="flex items-center gap-2 rounded-2xl bg-white p-1.5 pl-4 shadow-lg ring-1 ring-black/5 focus-within:ring-2 focus-within:ring-white/70 transition-shadow">
                <Search className="w-5 h-5 text-secondary-400 shrink-0" />
                <input id="help-question" type="text" inputMode="search" enterKeyHint="search" autoComplete="off" value={query} maxLength={500} autoFocus
                  onChange={(e) => { setQuery(e.target.value); setResult(null); }}
                  placeholder="e.g. How do I stop patients booking more than a month ahead?"
                  className="flex-1 min-w-0 appearance-none border-0 bg-transparent px-1 py-2.5 text-base text-secondary-900 placeholder:text-secondary-400 outline-none focus:outline-none focus:ring-0 shadow-none" />
                {query && (
                  <button type="button" onClick={clear} aria-label="Clear" className="p-1.5 rounded-lg text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100">
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button type="submit" disabled={asking || !query.trim()}
                  className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-50 shrink-0">
                  {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  <span className="hidden sm:inline">Ask</span>
                </button>
              </div>
            </form>
            <p className="text-xs text-white/70 mt-2.5">Questions are about using the app. Please don't include patient names or details.</p>

            {!query && (
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-white/70 mr-1">Popular:</span>
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => ask(s)}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 ring-1 ring-white/25 transition-colors">{s}</button>
                ))}
              </div>
            )}
          </div>
        </section>

        {error && <p className="text-red-600">{error}</p>}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] items-start">
          {/* Main column */}
          <div className="space-y-6 min-w-0">
            {asking && (
              <div className="rounded-2xl border border-primary-200 bg-white p-5 flex items-center gap-3 text-secondary-600" aria-live="polite">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" /> Finding the answer…
              </div>
            )}

            {/* Answer */}
            {result && !asking && (
              <section className="space-y-4" aria-live="polite">
                <div className="rounded-2xl border border-secondary-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-secondary-100 bg-secondary-50">
                    <p className="text-sm text-secondary-600 min-w-0 truncate">You asked: <span className="font-medium text-secondary-900">{result.question}</span></p>
                    <button type="button" onClick={clear} className="text-sm text-primary-700 hover:underline shrink-0">New question</button>
                  </div>
                  <div className="p-5 flex gap-4">
                    <span className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${result.found ? "bg-primary-600 text-white" : "bg-amber-100 text-amber-700"}`}>
                      <Sparkles className="w-5 h-5" />
                    </span>
                    <div className="min-w-0 flex-1 space-y-4">
                      <p className="text-secondary-800 leading-relaxed whitespace-pre-line">
                        {result.answer || result.notice || (result.articles?.length ? "Here's what matches your question:" : "We couldn't find anything about that. Try other words.")}
                      </p>
                      {result.answer && goLinks.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {goLinks.map((a) => (
                            <button key={a.id} type="button" onClick={() => navigate(a.link.path)}
                              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700">
                              {a.link.label} <ArrowRight className="w-4 h-4" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Help couldn't do it: offer to send it on. Off-topic (clinical) questions get no request. */}
                {result.request ? (
                  <SendRequestCard key={result.question} question={result.question} request={result.request} isAdmin={isAdmin} onSent={sent} />
                ) : result.answer && result.found && (
                  <SendRequestCard key={result.question} question={result.question} request={{ kind: "other" }} isAdmin={isAdmin} compact onSent={sent} />
                )}

                {result.articles?.length > 0 && (
                  <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-secondary-500">{result.answer ? "Read more" : "Matching articles"}</h2>
                    {result.articles.map((a, i) => <HelpArticleCard key={a.id} article={a} defaultOpen={!result.answer && i === 0} />)}
                  </div>
                )}
              </section>
            )}

            {/* Matches while typing */}
            {!result && !asking && instant.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-secondary-500">Matching articles · press Ask for a direct answer</h2>
                {instant.map((a, i) => <HelpArticleCard key={a.id} article={a} defaultOpen={i === 0} />)}
              </section>
            )}

            {/* Topics */}
            {library && !result && !asking && instant.length === 0 && (
              category ? (
                <section className="space-y-4">
                  <button type="button" onClick={() => setCategory(null)} className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
                    <ArrowLeft className="w-4 h-4" /> All topics
                  </button>
                  <div className="flex items-center gap-3">
                    {(() => { const Icon = (TOPICS[category] || [BookOpen])[0]; return (
                      <span className="w-11 h-11 rounded-xl bg-primary-600 text-white flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                    ); })()}
                    <div>
                      <h2 className="text-xl font-heading font-bold text-secondary-900">{category}</h2>
                      {TOPICS[category] && <p className="text-sm text-secondary-500">{TOPICS[category][1]}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {articles.filter((a) => a.category === category).map((a) => <HelpArticleCard key={a.id} article={a} />)}
                  </div>
                </section>
              ) : (
                <section className="space-y-4">
                  <h2 className="text-xl font-heading font-bold text-secondary-900">Browse by topic</h2>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {categories.map((c) => (
                      <TopicTile key={c} name={c} articles={articles.filter((a) => a.category === c)} onOpen={() => setCategory(c)} />
                    ))}
                  </div>
                </section>
              )
            )}
            {!library && !error && (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-44 rounded-2xl bg-secondary-100 animate-pulse" />)}
              </div>
            )}
          </div>

          {/* Side column: requests, as one list-style panel so it doesn't read as another topic */}
          <aside className="space-y-4 lg:sticky lg:top-4">
            <h2 className="text-xl font-heading font-bold text-secondary-900">Requests</h2>
            <div className="rounded-2xl border border-secondary-200 bg-white divide-y divide-secondary-100 overflow-hidden">
              {isAdmin && (
                <Link to="/help/requests" className="group flex items-center gap-3 px-4 py-3.5 hover:bg-secondary-50 transition-colors">
                  <span className="relative w-9 h-9 shrink-0 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <Inbox className="w-4 h-4" />
                    {teamOpen > 0 && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-secondary-900">From your team</span>
                    <span className="block text-xs text-secondary-500">
                      {teamOpen > 0 ? `${teamOpen} waiting for your review` : "Nothing waiting for review"}
                    </span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-secondary-400 group-hover:text-secondary-700" />
                </Link>
              )}

              <div className="px-4 py-3.5 space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-500">Sent by you</p>
                <MyRequests refreshKey={requestsVersion} compact />
              </div>

              <div className="px-4 py-3.5 bg-secondary-50 space-y-2.5">
                {askingSupport ? (
                  <SendRequestCard question={query} request={{ kind: "other" }} isAdmin={isAdmin} bare onSent={sent} />
                ) : (
                  <>
                    <p className="text-sm text-secondary-700">
                      <span className="font-semibold text-secondary-900">Can't find it?</span>{" "}
                      Tell {isAdmin ? "the MindConnect team" : "your clinic admin"} what you were trying to do.
                    </p>
                    <button type="button" onClick={() => setAskingSupport(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-secondary-300 text-secondary-800 text-sm font-medium px-3 py-1.5 hover:border-primary-400 hover:text-primary-700">
                      <MessageSquarePlus className="w-4 h-4" /> Send a request
                    </button>
                  </>
                )}
              </div>
            </div>

            {isAdmin && (
              <Link to="/getting-started" className="inline-flex items-center gap-1.5 text-sm text-primary-700 hover:underline">
                <Rocket className="w-4 h-4" /> Getting Started checklist <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}
